/**
 * People IQ — Employee List page.
 * HR system-of-records inside Asset IQ.
 *
 * Sensitive fields (PAN / Aadhaar / bank) are already masked by the backend
 * for viewers without the `view_employee_sensitive` capability, so this page
 * just renders whatever the API returns.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasCapability, formatDate } from "@/lib/permissions";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { UserPlus, Users as UsersIcon, Search, Lock, Building2, Filter, ChevronRight, LayoutGrid, ClipboardCheck, Upload, KeyRound, IndianRupee } from "lucide-react";
import * as XLSX from "xlsx";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const LIFECYCLE_LABELS = {
  candidate: { label: "Candidate", tint: "bg-slate-100 text-slate-700" },
  active: { label: "Active", tint: "bg-emerald-100 text-emerald-700" },
  probation: { label: "Probation", tint: "bg-amber-100 text-amber-800" },
  confirmed: { label: "Confirmed", tint: "bg-blue-100 text-blue-700" },
  transferred: { label: "Transferred", tint: "bg-violet-100 text-violet-700" },
  suspended: { label: "Suspended", tint: "bg-rose-100 text-rose-700" },
  resigned: { label: "Resigned", tint: "bg-orange-100 text-orange-700" },
  terminated: { label: "Terminated", tint: "bg-red-100 text-red-800" },
  archived: { label: "Archived", tint: "bg-slate-200 text-slate-600" },
};

export default function PeopleList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", lifecycle_state: "all", department_id: "all" });

  const canCreate = hasCapability(user, "create_employee");
  const canManageSalary = hasCapability(user, "manage_employee_salary");
  const [provOpen, setProvOpen] = useState(false);
  const [salaryOpen, setSalaryOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.q) params.q = filters.q;
      if (filters.lifecycle_state && filters.lifecycle_state !== "all") params.lifecycle_state = filters.lifecycle_state;
      if (filters.department_id && filters.department_id !== "all") params.department_id = filters.department_id;
      const [{ data: emps }, { data: depts }] = await Promise.all([
        api.get("/people/employees", { params }),
        api.get("/people/org/departments"),
      ]);
      setRows(emps);
      setDepartments(depts);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filters.lifecycle_state, filters.department_id]);
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); }, [filters.q]);

  const stats = useMemo(() => {
    const c = { total: rows.length, active: 0, probation: 0, exited: 0, finalized: 0 };
    for (const r of rows) {
      if (["active", "confirmed"].includes(r.lifecycle_state)) c.active++;
      if (r.lifecycle_state === "probation") c.probation++;
      if (["resigned", "terminated", "archived"].includes(r.lifecycle_state)) c.exited++;
      if (r.is_finalized) c.finalized++;
    }
    return c;
  }, [rows]);

  const deptMap = useMemo(() => Object.fromEntries(departments.map((d) => [d.id, d.name])), [departments]);

  return (
    <div className="space-y-6" data-testid="people-list-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">People IQ</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Employee Directory</h1>
          <p className="text-slate-500 mt-1 text-sm">{rows.length} employee(s) · HR system of records</p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" data-testid="dir-btn">
            <Link to="/people/directory"><LayoutGrid className="w-4 h-4 mr-2" /> Directory</Link>
          </Button>
          {hasCapability(user, "manage_checklist_templates") && (
            <Button asChild variant="outline" data-testid="templates-btn">
              <Link to="/people/templates"><ClipboardCheck className="w-4 h-4 mr-2" /> Checklists</Link>
            </Button>
          )}
          {hasCapability(user, "manage_org_structure") && (
            <Button asChild variant="outline" data-testid="org-masters-btn">
              <Link to="/people/org">
                <Building2 className="w-4 h-4 mr-2" /> Org Structure
              </Link>
            </Button>
          )}
          {canCreate && (
            <>
              <Button asChild variant="outline" data-testid="bulk-upload-nav-btn">
                <Link to="/people/bulk-upload"><Upload className="w-4 h-4 mr-2" /> Bulk Upload</Link>
              </Button>
              <Button variant="outline" onClick={() => setProvOpen(true)} data-testid="provision-logins-btn">
                <KeyRound className="w-4 h-4 mr-2" /> Create Logins
              </Button>
              {canManageSalary && (
                <Button variant="outline" onClick={() => setSalaryOpen(true)} data-testid="salary-upload-nav-btn">
                  <IndianRupee className="w-4 h-4 mr-2" /> Salary Upload
                </Button>
              )}
              <Button
                className="bg-[#7C1D3A] hover:bg-[#6c1c36]"
                onClick={() => navigate("/people/new")}
                data-testid="new-employee-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" /> New Employee
              </Button>
            </>
          )}
        </div>
      </div>

      <ProvisionLoginsDialog open={provOpen} onClose={() => setProvOpen(false)} onDone={load} />
      <SalaryBulkUploadDialog open={salaryOpen} onClose={() => setSalaryOpen(false)} />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Tile label="Total" value={stats.total} tint="slate" />
        <Tile label="Active" value={stats.active} tint="emerald" />
        <Tile label="Probation" value={stats.probation} tint="amber" />
        <Tile label="Exited" value={stats.exited} tint="rose" />
        <Tile label="Finalized" value={stats.finalized} tint="blue" icon={<Lock className="w-3 h-3" />} />
      </div>

      <Card>
        <CardHeader className="border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="people-search"
                placeholder="Search by code, name, email, phone…"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                className="pl-8"
              />
            </div>
            <Select value={filters.lifecycle_state} onValueChange={(v) => setFilters({ ...filters, lifecycle_state: v })}>
              <SelectTrigger className="w-[170px]" data-testid="filter-state"><SelectValue placeholder="State" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All states</SelectItem>
                {Object.entries(LIFECYCLE_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.department_id} onValueChange={(v) => setFilters({ ...filters, department_id: v })}>
              <SelectTrigger className="w-[200px]" data-testid="filter-dept"><SelectValue placeholder="Department" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All departments</SelectItem>
                {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {(filters.q || filters.lifecycle_state !== "all" || filters.department_id !== "all") && (
              <Button variant="ghost" size="sm" onClick={() => setFilters({ q: "", lifecycle_state: "all", department_id: "all" })}>
                <Filter className="w-3.5 h-3.5 mr-1" /> Clear
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3">Code</th>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Designation</th>
                  <th className="text-left px-4 py-3">Department</th>
                  <th className="text-left px-4 py-3">DOJ</th>
                  <th className="text-left px-4 py-3">State</th>
                  <th className="text-right px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody data-testid="people-tbody">
                {loading ? (
                  <tr><td colSpan="7" className="text-center text-slate-400 py-10">Loading…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan="7" className="text-center py-16">
                    <UsersIcon className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <div className="text-slate-500 text-sm">No employees yet.</div>
                    {canCreate && <Button variant="link" onClick={() => navigate("/people/new")} className="text-[#7C1D3A]">Add the first one</Button>}
                  </td></tr>
                ) : rows.map((r) => {
                  const lc = LIFECYCLE_LABELS[r.lifecycle_state] || { label: r.lifecycle_state, tint: "bg-slate-100" };
                  return (
                    <tr
                      key={r.id}
                      onClick={() => navigate(`/people/${r.id}`)}
                      className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                      data-testid={`emp-row-${r.employee_code}`}
                    >
                      <td className="px-4 py-3 font-mono text-xs text-slate-700">
                        {r.employee_code || <span className="text-amber-600 not-italic">— pending —</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{r.full_name}</div>
                        {r.preferred_name && <div className="text-xs text-slate-500">"{r.preferred_name}"</div>}
                      </td>
                      <td className="px-4 py-3 text-slate-700">{r.designation_id ? "—" : "—"}</td>
                      <td className="px-4 py-3 text-slate-700">{deptMap[r.department_id] || "—"}</td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{formatDate(r.date_of_joining)}</td>
                      <td className="px-4 py-3">
                        <Badge variant="secondary" className={lc.tint}>{lc.label}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {r.is_finalized ? (
                          <Badge variant="secondary" className="bg-blue-100 text-blue-700 gap-1"><Lock className="w-3 h-3" /> Locked</Badge>
                        ) : (
                          <Badge variant="outline" className="text-slate-500">Draft</Badge>
                        )}
                        <ChevronRight className="w-4 h-4 inline-block ml-2 text-slate-400" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Tile({ label, value, tint = "slate", icon }) {
  const bgMap = {
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    rose: "bg-rose-50 border-rose-200 text-rose-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
  };
  return (
    <div className={`rounded-lg border px-4 py-3 ${bgMap[tint] || bgMap.slate}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70 flex items-center gap-1">{icon}{label}</div>
      <div className="text-2xl font-bold font-heading mt-0.5">{value}</div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Create Logins dialog (Sep 2026) — provisions minimal `employee`-role Asset IQ
// accounts for staff who don't have one yet, links them, and emails each a
// welcome message with a temp password (force-reset on first login).
// -----------------------------------------------------------------------------
function ProvisionLoginsDialog({ open, onClose, onDone }) {
  const [list, setList] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setList(null);
    setResult(null);
    api.get("/people/employees/unlinked")
      .then(({ data }) => {
        setList(data);
        setSelected(new Set(data.filter((e) => e.provision_email).map((e) => e.id)));
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Failed to load"));
  }, [open]);

  const toggle = (id) => setSelected((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const run = async () => {
    if (selected.size === 0) return toast.error("Select at least one employee");
    setBusy(true);
    try {
      const { data } = await api.post("/people/employees/provision-logins", { employee_ids: [...selected] });
      setResult(data);
      if (data.created || data.linked_existing) {
        toast.success(`Created ${data.created} login(s), linked ${data.linked_existing} existing account(s)`);
        onDone?.();
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Provisioning failed");
    } finally { setBusy(false); }
  };

  const withEmail = (list || []).filter((e) => e.provision_email);
  const noEmail = (list || []).filter((e) => !e.provision_email);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="provision-logins-dialog">
        <DialogHeader>
          <DialogTitle>Create Employee Logins</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-slate-500 leading-relaxed">
          Each selected employee gets an Asset IQ login with the minimal <b>Employee</b> role
          (My Profile only), linked to their record. They receive a welcome email with a
          temporary password and must set a new one on first login. If an account with the
          same email already exists, it is simply linked.
        </div>
        {list === null ? (
          <div className="py-8 text-center text-slate-400 text-sm">Loading…</div>
        ) : result ? (
          <div className="space-y-2 text-sm" data-testid="provision-result">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-800">
              Created <b>{result.created}</b> new login(s) · linked <b>{result.linked_existing}</b> existing · skipped {result.skipped}
            </div>
            {result.errors?.map((e, i) => (
              <div key={i} className="rounded-md border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs text-rose-700">
                {e.name || e.employee_id}: {e.error}
              </div>
            ))}
          </div>
        ) : list.length === 0 ? (
          <div className="py-8 text-center text-slate-400 text-sm" data-testid="provision-empty">
            Every employee already has a linked account.
          </div>
        ) : (
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={withEmail.length > 0 && withEmail.every((e) => selected.has(e.id))}
                onChange={(e) => setSelected(e.target.checked ? new Set(withEmail.map((x) => x.id)) : new Set())}
                data-testid="provision-select-all"
              />
              Select all ({withEmail.length})
            </label>
            <div className="max-h-64 overflow-y-auto divide-y divide-slate-100 rounded-md border border-slate-200">
              {withEmail.map((e) => (
                <label key={e.id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer" data-testid={`provision-row-${e.id}`}>
                  <input type="checkbox" checked={selected.has(e.id)} onChange={() => toggle(e.id)} data-testid={`provision-check-${e.id}`} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">
                      {e.full_name} {e.employee_code && <span className="text-[10px] font-mono text-slate-400">#{e.employee_code}</span>}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{e.provision_email}</div>
                  </div>
                </label>
              ))}
              {noEmail.map((e) => (
                <div key={e.id} className="flex items-center gap-3 px-3 py-2 opacity-50" title="No email on record">
                  <input type="checkbox" disabled />
                  <div className="min-w-0">
                    <div className="text-sm text-slate-600 truncate">{e.full_name}</div>
                    <div className="text-xs text-rose-500">No email on record — cannot create login</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="provision-close-btn">
            {result ? "Close" : "Cancel"}
          </Button>
          {!result && (
            <Button onClick={run} disabled={busy || selected.size === 0} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="provision-run-btn">
              {busy ? "Creating…" : `Create ${selected.size} Login${selected.size === 1 ? "" : "s"}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Salary bulk upload dialog (Sep 2026) — Excel/CSV salary revision upload with
// dry-run, mirroring the employee bulk-upload flow.
// -----------------------------------------------------------------------------
const SALARY_COLUMNS = ["employee_code", "financial_year", "gross_salary", "basic", "hra", "effective_from", "revision_note"];

function SalaryBulkUploadDialog({ open, onClose }) {
  const [fileName, setFileName] = useState(null);
  const [rows, setRows] = useState(null);
  const [parseErrors, setParseErrors] = useState([]);
  const [dryResult, setDryResult] = useState(null);
  const [finalResult, setFinalResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setFileName(null); setRows(null); setParseErrors([]); setDryResult(null); setFinalResult(null); };
  useEffect(() => { if (open) reset(); }, [open]);

  const downloadCsvTemplate = async () => {
    try {
      const token = localStorage.getItem("assetiq_token");
      const res = await fetch(`${api.defaults.baseURL}/people/salary/bulk-upload/template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch template");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "people_iq_salary_template.csv";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  const downloadXlsxTemplate = () => {
    const fy = (() => { const d = new Date(); const s = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1; return `${s}-${s + 1}`; })();
    const ws = XLSX.utils.aoa_to_sheet([
      SALARY_COLUMNS,
      ["1001", fy, 600000, 360000, 240000, `${fy.slice(0, 4)}-04-01`, "Annual revision"],
    ]);
    ws["!cols"] = SALARY_COLUMNS.map((c) => ({ wch: Math.max(c.length + 2, 16) }));
    const notes = XLSX.utils.aoa_to_sheet([
      ["People IQ salary bulk upload — column guide"],
      [],
      ["Column", "Mandatory?", "Notes"],
      ["employee_code", "YES", "Numeric employee code of an existing staff record"],
      ["financial_year", "YES", "Format YYYY-YYYY, April to March, e.g. 2026-2027"],
      ["gross_salary", "YES", "Annual gross. When basic + hra are filled, they must equal gross exactly"],
      ["basic", "optional", "Annual basic. Leave blank (with hra) for a gross-only upload; backfill later by re-uploading the same employee + FY + date"],
      ["hra", "optional", "Annual HRA. Leave blank (with basic) for a gross-only upload"],
      ["effective_from", "optional", "YYYY-MM-DD within the FY; blank = 1 April of the FY. Same employee + FY + date overwrites the earlier record"],
      ["revision_note", "optional", "e.g. Annual appraisal, Mid-year hike"],
    ]);
    notes["!cols"] = [{ wch: 20 }, { wch: 14 }, { wch: 80 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Salary");
    XLSX.utils.book_append_sheet(wb, notes, "Notes");
    XLSX.writeFile(wb, "people_iq_salary_template.xlsx");
    toast.success("Excel template downloaded");
  };

  const handleFile = async (f) => {
    if (!f) return;
    setFileName(f.name); setRows(null); setParseErrors([]); setDryResult(null); setFinalResult(null);
    try {
      const wb = XLSX.read(await f.arrayBuffer());
      const json = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: "" });
      if (json.length === 0) { setParseErrors(["File is empty"]); return; }
      const missing = ["employee_code", "financial_year", "gross_salary"].filter((c) => !(c in json[0]));
      if (missing.length) { setParseErrors([`Missing required column(s): ${missing.join(", ")}`]); return; }
      setRows(json.map((r) => ({
        employee_code: String(r.employee_code ?? "").trim(),
        financial_year: String(r.financial_year ?? "").trim(),
        gross_salary: r.gross_salary === "" ? 0 : Number(r.gross_salary),
        basic: r.basic === "" ? null : Number(r.basic),
        hra: r.hra === "" ? null : Number(r.hra),
        effective_from: String(r.effective_from ?? "").trim() || null,
        revision_note: String(r.revision_note ?? "").trim() || null,
      })));
    } catch { setParseErrors(["Could not parse file — use the CSV or Excel template"]); }
  };

  const submit = async (dryRun) => {
    setBusy(true);
    try {
      const { data } = await api.post("/people/salary/bulk-upload", { rows, dry_run: dryRun });
      if (dryRun) {
        setDryResult(data);
        toast.success(`Dry run: ${data.created} valid, ${data.skipped} skipped`);
      } else {
        setFinalResult(data);
        toast.success(`Recorded ${data.created} salary revision(s)`);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="salary-upload-dialog">
        <DialogHeader><DialogTitle>Salary Revision — Bulk Upload</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={downloadCsvTemplate} data-testid="salary-csv-template-btn">CSV Template</Button>
            <Button variant="outline" size="sm" onClick={downloadXlsxTemplate} data-testid="salary-xlsx-template-btn">Excel Template (.xlsx)</Button>
          </div>
          <div className="text-xs text-slate-500 rounded-md border border-slate-200 bg-slate-50 p-3 leading-relaxed">
            Required: <b>employee_code</b>, <b>financial_year</b> (e.g. 2026-2027), <b>gross_salary</b>.
            <b>basic</b> / <b>hra</b> are optional — leave both blank for a gross-only record and backfill the breakup
            later; when filled, Basic + HRA must equal Gross exactly. <b>effective_from</b> optional (defaults to
            1 April of the FY) — re-uploading the same employee + FY + date updates that record (this is also the
            one-time breakup backfill path).
          </div>
          <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
            <input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="salary-file-input" className="text-sm" />
            {fileName && <div className="text-xs text-slate-500 mt-2">{fileName} — {rows?.length || 0} rows</div>}
          </div>
          {parseErrors.map((e, i) => <div key={i} className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded px-3 py-2">{e}</div>)}
          {rows && !finalResult && (
            <>
              <div className="overflow-x-auto max-h-56 overflow-y-auto rounded-md border border-slate-200">
                <table className="w-full text-xs" data-testid="salary-preview-table">
                  <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                    <tr>{["Code", "FY", "Gross", "Basic", "HRA", "Effective", "Note"].map((h) => <th key={h} className="px-2 py-1.5 text-left">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 50).map((r, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-2 py-1 font-mono">{r.employee_code}</td>
                        <td className="px-2 py-1">{r.financial_year}</td>
                        <td className="px-2 py-1 text-right">{r.gross_salary}</td>
                        <td className="px-2 py-1 text-right">{r.basic ?? "—"}</td>
                        <td className="px-2 py-1 text-right">{r.hra ?? "—"}</td>
                        <td className="px-2 py-1">{r.effective_from || "—"}</td>
                        <td className="px-2 py-1 text-slate-500">{r.revision_note || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rows.length > 50 && <div className="text-[11px] text-slate-400">Showing first 50 of {rows.length} rows</div>}
            </>
          )}
          {(dryResult || finalResult) && (
            <div className="space-y-1.5" data-testid="salary-upload-result">
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-800 text-xs font-medium">
                {(finalResult || dryResult).created} valid · {(finalResult || dryResult).skipped} skipped
                {dryResult && !finalResult && " (dry run — nothing saved yet)"}
              </div>
              {(finalResult || dryResult).errors?.map((e, i) => (
                <div key={i} className="rounded-md border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs text-rose-700">
                  Row {e.row}: {e.error}
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {rows && !finalResult && (
            <>
              <Button variant="outline" onClick={() => submit(true)} disabled={busy} data-testid="salary-dry-run-btn">
                {busy ? "Validating…" : "Validate (dry run)"}
              </Button>
              <Button onClick={() => submit(false)} disabled={busy} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="salary-upload-btn">
                {busy ? "Uploading…" : `Upload ${rows.length} Revisions`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
